<!--
  VerdictBadge — the one way a computed verdict is shown.

  Tones map to the spec's acceptance language:
    pass  — inside CSA A23.1 +/-1%, or spread/flow in range
    fail  — outside tolerance
    warn  — out of range with a corrective instruction (add/reduce water)
    info  — a computed number with deliberately NO verdict (mud balance, W/C)
    muted — not enough input entered yet to compute anything

  A badge never blocks anything; it reports.
-->
<script lang="ts">
  type Tone = 'pass' | 'fail' | 'warn' | 'info' | 'muted';

  interface Props {
    tone?: Tone;
    /** Short word: 'PASS', 'FAIL', 'W/C', 'IN RANGE'. */
    label: string;
    /** The number or instruction, e.g. '0.181' or 'Add water'. */
    detail?: string;
    compact?: boolean;
  }

  let { tone = 'muted', label, detail = '', compact = false }: Props = $props();
</script>

<span class="badge tone-{tone}" class:compact role="status">
  <span class="label">{label}</span>
  {#if detail}<span class="detail">{detail}</span>{/if}
</span>

<style>
  .badge {
    display: inline-flex;
    align-items: baseline;
    gap: 0.4em;
    padding: 0.3rem 0.55rem;
    border-radius: 0.4rem;
    border: 2px solid currentColor;
    font-size: 0.875rem;
    line-height: 1.15;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .compact {
    padding: 0.15rem 0.4rem;
    font-size: 0.8125rem;
    border-width: 1px;
  }

  .label {
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-size: 0.75em;
  }

  .detail {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--fg, #10151b);
  }

  .tone-pass {
    color: var(--good, #1a7f45);
    background: var(--good-bg, #e7f6ed);
  }
  .tone-fail {
    color: var(--bad, #b3261e);
    background: var(--bad-bg, #fdecea);
  }
  .tone-warn {
    color: var(--warn, #8a4b00);
    background: var(--warn-bg, #fff6e6);
  }
  .tone-info {
    color: var(--accent, #0b63c5);
    background: var(--accent-bg, #e8f1fb);
  }
  .tone-muted {
    color: var(--fg-muted, #3f4750);
    background: var(--surface-muted, #eef1f4);
  }
</style>
